import React, { useEffect, useMemo, useState } from "react";
import { resolveRedirect } from "./config";

/** The accounts app no longer signs anyone in.
 *
 * It used to host its own login widget, which meant two places in the estate
 * could create a session and both had to be kept correct. Sign-in now lives in
 * one app, and the session cookie is scoped to the parent domain, so a session
 * created there is already valid here and everywhere else.
 *
 * What remains is the part that only this host can do: honour an inbound
 * `?redirect=` and carry it through sign-in, so a link into this app still ends
 * up where it was pointed.
 */
export default function FortisAuth() {
  const [error, setError] = useState("");

  const destination = useMemo(() => {
    try {
      const origins = (process.env.REACT_APP_ALLOWED_REDIRECT_ORIGINS || "").split(",").filter(Boolean);
      return resolveRedirect(
        new URLSearchParams(window.location.search).get("redirect"),
        process.env.REACT_APP_REDIRECT_URL || "",
        origins,
        window.location.origin,
      );
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "This sign-in link is not valid.");
      return "";
    }
  }, []);

  useEffect(() => {
    if (!destination) return;
    const mainApp = process.env.REACT_APP_MAIN_APP_URL;
    if (!mainApp) {
      setError("Sign-in is not configured.");
      return;
    }
    const signIn = new URL("/sign-in", mainApp);
    signIn.searchParams.set("redirect", "/");
    window.location.assign(signIn.toString());
  }, [destination]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-12">
      <section className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-emerald-900">
          {process.env.REACT_APP_BRAND_NAME || "Fortis"}
        </h1>
        {error ? <p role="alert">{error}</p> : <p className="text-slate-600">Taking you to sign in…</p>}
      </section>
    </main>
  );
}
